// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Main component for the item data upload feature
 */
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useSupportError } from '@/hooks/use-support-error';
import {
  ItemDataUploadResult,
  uploadItemData,
  uploadItemDataResponseSchema,
} from '@/items/api/upload-item-data';
import { CsvUpload } from '@/items/components/item-data-upload/csv-upload';
import { Results } from '@/items/components/item-data-upload/results';
import { Stepper } from '@/items/components/item-data-upload/stepper';
import { TemplateDownload } from '@/items/components/item-data-upload/template-download';
import type {
  ExpandedItemsState,
  Step,
} from '@/items/components/item-data-upload/types';
import { itemsQueryKey } from '@/items/hooks/use-items';
import { Box } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useRef, useState } from 'react';

interface Props {
  onClose: () => void;
}

/**
 * Component for uploading item data from CSV files.
 * Provides a multi-step interface for downloading a CSV template,
 * uploading the filled template, and viewing results.
 */
export function ItemDataUpload(props: Props) {
  const queryClient = useQueryClient();
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<ItemDataUploadResult | null>(
    null
  );
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [expandedItems, setExpandedItems] = useState<ExpandedItemsState>({
    created: false,
    failed: false,
    structureErrors: false,
  });
  const [hasFatalError, setHasFatalError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearFile = () => {
    setFile(null);
    setUploadLineCount(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const { mutate: uploadItems, isPending: isUploading } = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error('Missing file');
      }

      const formData = new FormData();
      formData.append('data', file);

      return uploadItemData(formData);
    },
    onSuccess: (data) => {
      clearFile();
      setUploadResult(data);
      setUploadErrorMessage(null);
      setCurrentStep('results');
      queryClient.invalidateQueries({ queryKey: itemsQueryKey });
      queryClient.invalidateQueries({ queryKey: ['item'] });
    },
    onError: (error: AxiosError) => {
      clearFile();

      if (isPlanLimitError(error)) {
        return;
      }

      // First try to parse as a response with validation errors
      const knownError = uploadItemDataResponseSchema.safeParse(
        error.response?.data
      );
      if (knownError.success) {
        // This is a known error that comes back due to the fact it rolls back the transaction if any of the rows are bad.
        setUploadResult(knownError.data.data);
        setUploadErrorMessage(null);
        setCurrentStep('results');
        return;
      }

      // Could not parse as validation errors - use generic error handler
      const apiError = handleAPIError(error);

      // Curated error code from ERROR_MESSAGES / VIOLATION_ERROR_MESSAGES:
      // show inline in dialog. Everything else (unmapped 4xx, 5xx, network)
      // rolls up to the support snackbar so backend text never leaks.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }

      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while uploading item data.');
    },
  });

  /**
   * Counts lines in a file (excluding header)
   */
  const countFileLines = async (file: File): Promise<number> => {
    const text = await file.text();
    return text.split('\n').length - 1; // -1 to exclude header row
  };

  /**
   * Handles file selection for upload
   */
  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setFile(selectedFile);
      const count = await countFileLines(selectedFile);
      setUploadLineCount(count);
    }
  };

  /**
   * Triggers the file input click
   */
  const handleSelectFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Handles the upload process
   */
  const handleUpload = () => {
    if (file) {
      uploadItems();
    }
  };

  /**
   * Resets the form to start a new upload
   */
  const handleStartNew = () => {
    setCurrentStep('download-template');
    clearFile();
    setUploadResult(null);
    setExpandedItems({
      created: false,
      failed: false,
      structureErrors: false,
    });
  };

  /**
   * Toggles the expanded state of item lists
   */
  const toggleExpanded = (section: keyof ExpandedItemsState) => {
    setExpandedItems((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stepper currentStep={currentStep} />

      {currentStep === 'download-template' && (
        <TemplateDownload
          onClose={props.onClose}
          onNext={() => {
            clearFile();
            setCurrentStep('upload-csv');
          }}
        />
      )}

      {currentStep === 'upload-csv' && (
        <CsvUpload
          uploadErrorMessage={uploadErrorMessage}
          file={file}
          isUploading={isUploading}
          onSelectFile={handleSelectFile}
          onUpload={handleUpload}
          onBack={() => {
            setCurrentStep('download-template');
            setHasFatalError(false);
          }}
          onClose={props.onClose}
          hasFatalError={hasFatalError}
          uploadLineCount={uploadLineCount}
        />
      )}

      {currentStep === 'results' && (
        <Results
          uploadResult={uploadResult}
          expandedItems={expandedItems}
          onToggleExpanded={toggleExpanded}
          onBack={() => setCurrentStep('upload-csv')}
          onClose={props.onClose}
          onStartNew={handleStartNew}
        />
      )}

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleFileChange}
      />
    </Box>
  );
}
